// WorkspaceMembers — the everyday "who's in this workspace" view, pure.
//
// RECONCILED to the LIVE FLAT backend (IDD 94442b9d): a single-level
// workspace, roles exactly "owner" | "member"
// (packages/server/src/auth/members.ts), no ancestor-scope inheritance to
// footnote (that machinery belongs to core's DORMANT tenant›workspace›project
// model). The owner can never be removed from their own workspace (there is
// no "transfer ownership" route); every other member can be, and only by the
// owner — the caller decides who may see the remove affordance by what it
// passes as `members` and `onRequestRemove`, this view just renders the list.
//
// Removal is confirmed, never one click: `onRequestRemove` opens the
// confirm, a separate `onConfirmRemove` actually revokes. Revoke ≠ delete
// — the confirm copy says so explicitly.
//
// Pure/props-driven, fully controlled: `confirmingRemoveUserId` is the one
// piece of UI-navigation state, held by the caller — no internal state, no
// I/O.

export type { WorkspaceMembersProps };
export { WorkspaceMembers };

import { Plural, Trans, useLingui } from "@lingui/react/macro";
import type { WorkspaceMember } from "../../../app/ports/iam.js";
import { Button } from "../../Button/Button.js";
import { Dialog } from "../../Dialog/Dialog.js";
import { Heading } from "../../Heading/Heading.js";
import { Icon } from "../../Icon/Icon.js";
import { IconButton } from "../../IconButton/IconButton.js";
import { Text } from "../../Text/Text.js";
import { useWorkspaceRoleLabel } from "../useWorkspaceRoleLabel.js";
import "./WorkspaceMembers.css";

// `isYou` and `canRemove` arrive pre-computed on each row — the caller
// already knows which userId is the viewer's own and which role is "owner",
// so this pure view never re-derives identity/authorization from a raw list.
type WorkspaceMembersRow = WorkspaceMember & {
    readonly isYou: boolean;
    readonly canRemove: boolean;
};

type WorkspaceMembersProps = {
    readonly members: readonly WorkspaceMembersRow[];
    readonly confirmingRemoveUserId?: string;
    // Omitted entirely (not a boolean flag) when the viewer isn't the
    // workspace owner — the live backend refuses the invite route to
    // anyone else, so a member never sees a control they'd only get
    // "forbidden" from.
    readonly onInvite?: () => void;
    readonly onRequestRemove: (userId: string) => void;
    readonly onConfirmRemove: (userId: string) => void;
    readonly onCancelRemove: () => void;
    readonly className?: string;
};

const initialsOf = (email: string): string => email.slice(0, 2).toUpperCase();

const trashGlyph = <Icon name="trash" size="sm" />;

const WorkspaceMembers = ({
    members,
    confirmingRemoveUserId,
    onInvite,
    onRequestRemove,
    onConfirmRemove,
    onCancelRemove,
    className,
}: WorkspaceMembersProps) => {
    const { t } = useLingui();
    const roleLabel = useWorkspaceRoleLabel();

    const confirming = members.find((m) => m.userId === confirmingRemoveUserId);

    return (
        <div
            className={`bilda-iam-workspace-members${className ? ` ${className}` : ""}`}
        >
            <div className="bilda-iam-workspace-members__header">
                <div>
                    <Heading level={1} size="lg">
                        <Trans>Members</Trans>
                    </Heading>
                    <Text tone="dim" size="sm">
                        <Plural
                            value={members.length}
                            one="# member"
                            other="# members"
                        />
                    </Text>
                </div>
                {onInvite ? (
                    <Button variant="primary" size="sm" onClick={onInvite}>
                        <Trans>Invite</Trans>
                    </Button>
                ) : null}
            </div>

            <ul className="bilda-iam-workspace-members__list">
                {members.map((member) => (
                    <li
                        key={member.userId}
                        className="bilda-iam-workspace-members__row"
                    >
                        <span
                            className="bilda-iam-workspace-members__avatar"
                            aria-hidden="true"
                        >
                            {initialsOf(member.email)}
                        </span>
                        <span className="bilda-iam-workspace-members__identity">
                            <Text weight="medium" size="sm">
                                {member.email}
                                {member.isYou ? ` ${t`(you)`}` : ""}
                            </Text>
                        </span>
                        <Text
                            tone="faint"
                            size="xs"
                            className="bilda-iam-workspace-members__role"
                        >
                            {roleLabel(member.role)}
                        </Text>
                        {member.canRemove ? (
                            <IconButton
                                variant="ghost"
                                size="sm"
                                label={t`Remove ${member.email}`}
                                onClick={() => onRequestRemove(member.userId)}
                            >
                                {trashGlyph}
                            </IconButton>
                        ) : null}
                    </li>
                ))}
            </ul>

            <Dialog
                open={confirming != null}
                onOpenChange={(open) => {
                    if (!open) onCancelRemove();
                }}
                title={t`Remove member?`}
                description={
                    confirming
                        ? t`${confirming.email} loses access to this workspace. Everything they made stays — this only removes access.`
                        : undefined
                }
                footer={
                    <>
                        <Button variant="ghost" onClick={onCancelRemove}>
                            <Trans>Cancel</Trans>
                        </Button>
                        <Button
                            variant="danger"
                            onClick={() =>
                                confirming && onConfirmRemove(confirming.userId)
                            }
                        >
                            <Trans>Remove</Trans>
                        </Button>
                    </>
                }
            />
        </div>
    );
};
